class PredictiveSearch extends SearchForm {
  constructor() {
    super();
    this.cachedResults = {};
    this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    this.allPredictiveSearchInstances =
      document.querySelectorAll('predictive-search');
    this.isOpen = false;
    this.abortController = new AbortController();
    this.searchTerm = '';
    this.isMobile = window.innerWidth < 750;

    // Ensure predictiveSearchResults is initialized
    if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    }

    // For mobile, we need to create the container if it doesn't exist
    if (this.isMobile && !this.predictiveSearchResults) {
      console.log("Creating predictive search container for mobile");
      this.createMobileSearchContainer();
    }

    this.setupEventListeners();
  }

  // Create the search results container for mobile if it doesn't exist
  createMobileSearchContainer() {
    // Check if we already have a container
    if (this.querySelector('[data-predictive-search]')) {
      return;
    }

    console.log("Creating new predictive search container");
    
    // Create the container
    const container = document.createElement('div');
    container.classList.add('predictive-search', 'predictive-search--header');
    container.setAttribute('tabindex', '-1');
    container.setAttribute('data-predictive-search', '');
    
    // Add loading spinner
    const loadingState = document.createElement('div');
    loadingState.classList.add('predictive-search__loading-state');
    loadingState.innerHTML = `
      <svg aria-hidden="true" focusable="false" class="spinner" viewBox="0 0 66 66" xmlns="http://www.w3.org/2000/svg">
        <circle class="path" fill="none" stroke-width="6" cx="33" cy="33" r="30"></circle>
      </svg>
    `;
    
    container.appendChild(loadingState);
    
    // Add the container to the predictive-search element
    this.appendChild(container);
    
    // Update our reference
    this.predictiveSearchResults = container;
    
    console.log("Mobile search container created:", !!this.predictiveSearchResults);
  }

  setupEventListeners() {
    this.input.form.addEventListener('submit', this.onFormSubmit.bind(this));

    this.input.addEventListener('focus', this.onFocus.bind(this));
    this.addEventListener('focusout', this.onFocusOut.bind(this));
    this.addEventListener('keyup', this.onKeyup.bind(this));
    this.addEventListener('keydown', this.onKeydown.bind(this));
  }

  getQuery() {
    return this.input.value.trim();
  }

  onChange() {
    super.onChange();
    const newSearchTerm = this.getQuery();
    if (!this.searchTerm || !newSearchTerm.startsWith(this.searchTerm)) {
      // Remove the results when they are no longer relevant for the new search term
      // so they don't show up when the dropdown opens again
      this.querySelector("#predictive-search-results-groups-wrapper")?.remove();
    }

    // Update the term asap, don't wait for the predictive search query to finish loading
    this.updateSearchForTerm(this.searchTerm, newSearchTerm);

    this.searchTerm = newSearchTerm;

    if (!this.searchTerm.length) {
      this.close(true);
      return;
    }

    this.getSearchResults(this.searchTerm);
  }

  onFormSubmit(event) {
    if (!this.getQuery().length || this.querySelector('[aria-selected="true"] a')) event.preventDefault();
  }

  onFormReset(event) {
    super.onFormReset(event);
    if (super.shouldResetForm()) {
      this.searchTerm = '';
      this.abortController.abort();
      this.abortController = new AbortController();
      this.closeResults(true);
    }
  }

  onFocus() {
    const currentSearchTerm = this.getQuery();

    if (!currentSearchTerm.length) return;

    if (this.searchTerm !== currentSearchTerm) {
      // Search term was changed from other search input, treat it as a user change
      this.onChange();
    } else if (this.getAttribute('results') === 'true') {
      this.open();
    } else {
      this.getSearchResults(this.searchTerm);
    }
  }

  onFocusOut() {
    setTimeout(() => {
      if (!this.contains(document.activeElement)) this.close();
    })
  }

  onKeyup(event) {
    if (!this.getQuery().length) this.close(true);
    event.preventDefault();

    switch (event.code) {
      case 'ArrowUp':
        this.switchOption('up')
        break;
      case 'ArrowDown':
        this.switchOption('down');
        break;
      case 'Enter':
        this.selectOption();
        break;
    }
  }

  onKeydown(event) {
    // Prevent the cursor from moving in the input when using the up and down arrow keys
    if (
      event.code === 'ArrowUp' ||
      event.code === 'ArrowDown'
    ) {
      event.preventDefault();
    }
  }

  updateSearchForTerm(previousTerm, newTerm) {
    const searchForTextElement = this.querySelector(
      "[data-predictive-search-search-for-text]"
    );
    const currentButtonText = searchForTextElement?.innerText;
    if (currentButtonText) {
      if (currentButtonText.match(new RegExp(previousTerm, "g")).length > 1) {
        // The new term matches part of the button text and not just the search term, do not replace to avoid mistakes
        return;
      }
      const newButtonText = currentButtonText.replace(previousTerm, newTerm);
      searchForTextElement.innerText = newButtonText;
    }
  }

  switchOption(direction) {
    if (!this.getAttribute('open')) return;

    const moveUp = direction === 'up';
    const selectedElement = this.querySelector('[aria-selected="true"]');

    // Filter out hidden elements (duplicated page and article resources) thanks
    // to this https://developer.mozilla.org/en-US/docs/Web/API/HTMLElement/offsetParent
    const allVisibleElements = Array.from(
      this.querySelectorAll("li, button.predictive-search__item")
    ).filter((element) => element.offsetParent !== null);
    let activeElementIndex = 0;

    if (moveUp && !selectedElement) return;

    let selectedElementIndex = -1;
    let i = 0;

    while (selectedElementIndex === -1 && i <= allVisibleElements.length) {
      if (allVisibleElements[i] === selectedElement) {
        selectedElementIndex = i;
      }
      i++;
    }

    this.statusElement.textContent = "";

    if (!moveUp && selectedElement) {
      activeElementIndex =
        selectedElementIndex === allVisibleElements.length - 1
          ? 0
          : selectedElementIndex + 1;
    } else if (moveUp) {
      activeElementIndex =
        selectedElementIndex === 0
          ? allVisibleElements.length - 1
          : selectedElementIndex - 1;
    }

    if (activeElementIndex === selectedElementIndex) return;

    const activeElement = allVisibleElements[activeElementIndex];

    activeElement.setAttribute('aria-selected', true);
    if (selectedElement) selectedElement.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', activeElement.id);
  }

  selectOption() {
    const selectedOption = this.querySelector(
      '[aria-selected="true"] a, button[aria-selected="true"]'
    );

    if (selectedOption) selectedOption.click();
  }

  getSearchResults(searchTerm) {
    console.log("getSearchResults called for term:", searchTerm);
    const queryKey = searchTerm.toLowerCase();
    this.setLiveRegionLoadingState();

    if (this.cachedResults[queryKey]) {
      console.log("Using cached results for:", queryKey);
      this.renderSearchResults(this.cachedResults[queryKey]);
      return;
    }

    // Make sure predictiveSearchResults is initialized before making the fetch
    if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      console.log("Had to re-query for predictiveSearchResults before fetch:", !!this.predictiveSearchResults);
    }

    console.log("Fetching search results for:", searchTerm);
    fetch(`${window.shopUrl}/search/suggest?q=${encodeURIComponent(searchTerm)}&section_id=predictive-search`, {
      signal: this.abortController.signal
    })
      .then(response => {
        console.log("Search API response status:", response.status);
        if (!response.ok) {
          var error = new Error(response.status);
          throw error;
        }

        return response.text();
      })
      .then(text => {
        console.log("Got response text length:", text.length);
        const parser = new DOMParser();
        const doc = parser.parseFromString(text, 'text/html');
        const section = doc.querySelector('#shopify-section-predictive-search');
        
        if (section) {
          console.log("Found predictive-search section in response");
          const resultsMarkup = section.innerHTML;
          // Save a cached version of the results
          this.cachedResults[queryKey] = resultsMarkup;
          this.renderSearchResults(resultsMarkup);
        } else {
          console.error("Could not find #shopify-section-predictive-search in response");
          console.log("Response preview:", text.substring(0, 200) + "...");
        }
      })
      .catch(error => {
        console.error("Error in search fetch:", error);
        if (error?.code === 20) {
          // Code 20 means the call was aborted
          console.log("Search request was aborted");
          return;
        }
        this.close();
        throw error;
      });
  }

  renderSearchResults(resultsMarkup) {
    console.log("renderSearchResults called with markup length:", resultsMarkup.length);
    
    // For mobile, we need to create the container if it doesn't exist
    if (window.innerWidth < 750 && !this.predictiveSearchResults) {
      console.log("Creating container for mobile in renderSearchResults");
      this.createMobileSearchContainer();
    } else if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
      console.log("Had to re-query for predictiveSearchResults:", !!this.predictiveSearchResults);
    }
    
    if (this.predictiveSearchResults) {
      console.log("Setting innerHTML on predictiveSearchResults");
      // Clear any existing content first
      this.predictiveSearchResults.innerHTML = resultsMarkup;
      
      // Apply styling based on device
      if (window.innerWidth < 750) {
        console.log("Applying mobile styling");
        
        // Find the search modal and input field to position results properly
        const searchModal = this.closest('.search-modal');
        const searchForm = this.querySelector('form') || this.closest('form');
        const searchInput = searchForm ? searchForm.querySelector('input[type="search"]') : null;
        
        // Style the results to appear below the search bar
        this.predictiveSearchResults.style.display = 'block';
        this.predictiveSearchResults.style.position = 'absolute';
        this.predictiveSearchResults.style.top = '100%';
        this.predictiveSearchResults.style.left = '0';
        this.predictiveSearchResults.style.right = '0';
        this.predictiveSearchResults.style.width = '100%';
        this.predictiveSearchResults.style.maxHeight = '60vh';
        this.predictiveSearchResults.style.backgroundColor = 'white';
        this.predictiveSearchResults.style.zIndex = '100';
        this.predictiveSearchResults.style.borderTop = 'none';
        this.predictiveSearchResults.style.borderRadius = '0 0 8px 8px';
        this.predictiveSearchResults.style.boxShadow = '0 4px 10px rgba(0,0,0,0.1)';
        this.predictiveSearchResults.style.overflow = 'auto';
        
        // Ensure the modal has enough space
        if (searchModal) {
          searchModal.style.overflow = 'visible';
          const modalContent = searchModal.querySelector('.search-modal__content');
          if (modalContent) {
            modalContent.style.overflow = 'visible';
          }
        }
      } else {
        // Desktop styling
        this.predictiveSearchResults.style.display = 'block';
      }
      
      this.setAttribute('results', true);
      this.setLiveRegionResults();
      this.open();
      
      // Add event listeners to search-for buttons
      this.setupSearchForButtons();
      
      // Log the structure of the results
      console.log("Results structure:", {
        hasResultsWrapper: !!this.predictiveSearchResults.querySelector('.predictive-search__results-groups-wrapper'),
        childNodes: this.predictiveSearchResults.childNodes.length,
        innerHTML: this.predictiveSearchResults.innerHTML.substring(0, 100) + "..." // First 100 chars
      });
    } else {
      console.error("predictiveSearchResults is still null after re-query and creation attempt");
    }
  }
  
  // Add event listeners to the "Search for" buttons
  setupSearchForButtons() {
    const searchForButtons = this.querySelectorAll('.predictive-search__search-for-button');
    
    if (searchForButtons.length > 0) {
      console.log(`Found ${searchForButtons.length} search-for buttons, adding event listeners`);
      
      searchForButtons.forEach(button => {
        // Remove any existing listeners to prevent duplicates
        button.removeEventListener('click', this.handleSearchForButtonClick);
        
        // Add click event listener
        button.addEventListener('click', this.handleSearchForButtonClick.bind(this));
      });
    } else {
      console.log("No search-for buttons found");
    }
  }
  
  // Handle click on "Search for" button
  handleSearchForButtonClick(event) {
    event.preventDefault();
    
    const button = event.currentTarget;
    const searchTerm = button.dataset.searchTerm || button.textContent.trim();
    
    console.log(`Search for button clicked with term: ${searchTerm}`);
    
    if (searchTerm) {
      // Update the search input with the term
      if (this.input) {
        this.input.value = searchTerm;
      }
      
      // Submit the search form
      const form = button.closest('form') || this.querySelector('form');
      if (form) {
        form.submit();
      }
    }
  }

  setLiveRegionLoadingState() {
    this.statusElement = this.statusElement || this.querySelector('.predictive-search-status');
    this.loadingText = this.loadingText || this.getAttribute('data-loading-text');

    if (this.statusElement) {
      this.setLiveRegionText(this.loadingText);
    }
    this.setAttribute('loading', true);
  }

  setLiveRegionText(statusText) {
    if (!this.statusElement) {
      this.statusElement = this.querySelector('.predictive-search-status');
    }
    
    if (this.statusElement) {
      this.statusElement.setAttribute('aria-hidden', 'false');
      this.statusElement.textContent = statusText;

      setTimeout(() => {
        if (this.statusElement) {
          this.statusElement.setAttribute('aria-hidden', 'true');
        }
      }, 1000);
    }
  }

  setLiveRegionResults() {
    this.removeAttribute('loading');
    const countElement = this.querySelector('[data-predictive-search-live-region-count-value]');
    if (countElement) {
      this.setLiveRegionText(countElement.textContent);
    }
  }

  getResultsMaxHeight() {
    // Different calculation for mobile vs desktop
    if (window.innerWidth < 750) {
      // On mobile, use a percentage of viewport height
      return window.innerHeight * 0.6;
    } else {
      // On desktop, calculate based on header position
      const headerElement = document.querySelector('.section-header');
      if (headerElement) {
        this.resultsMaxHeight = window.innerHeight - headerElement.getBoundingClientRect().bottom;
        return this.resultsMaxHeight;
      }
      return 600; // Fallback height if header not found
    }
  }

  open() {
    // Make sure predictiveSearchResults is initialized before opening
    if (!this.predictiveSearchResults) {
      this.predictiveSearchResults = this.querySelector('[data-predictive-search]');
    }
    
    // Force the element to be displayed
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.style.display = 'block';
      
      // Set max height with some additional space for mobile
      const maxHeight = this.resultsMaxHeight || this.getResultsMaxHeight();
      this.predictiveSearchResults.style.maxHeight = `${maxHeight}px`;
    }
    
    this.setAttribute('open', true);
    this.input.setAttribute('aria-expanded', true);
    this.isOpen = true;
  }

  close(clearSearchTerm = false) {
    this.closeResults(clearSearchTerm);
    this.isOpen = false;
  }

  closeResults(clearSearchTerm = false) {
    if (clearSearchTerm) {
      this.input.value = '';
      this.removeAttribute('results');
    }
    const selected = this.querySelector('[aria-selected="true"]');

    if (selected) selected.setAttribute('aria-selected', false);

    this.input.setAttribute('aria-activedescendant', '');
    this.removeAttribute('loading');
    this.removeAttribute('open');
    this.input.setAttribute('aria-expanded', false);
    this.resultsMaxHeight = false;
    if (this.predictiveSearchResults) {
      this.predictiveSearchResults.removeAttribute('style');
    }
  }
}

customElements.define('predictive-search', PredictiveSearch);
